export const HISTORY_FIELDS = [
  "askingPrice",
  "revenue",
  "profit",
  "sde",
  "ebitda",
] as const;

export type HistoryField = (typeof HISTORY_FIELDS)[number];

export const HISTORY_FIELD_LABELS: Record<HistoryField, string> = {
  askingPrice: "Asking Price",
  revenue: "Revenue",
  profit: "Profit",
  sde: "SDE",
  ebitda: "EBITDA",
};

export type HistoryAmount = bigint | number | string | null | undefined;

export type HistorySnapshot = {
  id: number;
  createdAt: Date | string;
  source: string;
} & Record<HistoryField, HistoryAmount>;

export type HistoryChange =
  | {
      field: HistoryField;
      kind: "change";
      from: number;
      to: number;
      increased: boolean;
    }
  | { field: HistoryField; kind: "removed" }
  | { field: HistoryField; kind: "set"; to: number };

export type HistoryEvent = {
  id: number;
  createdAt: Date;
  source: string;
  changes: HistoryChange[];
};

export type HistoryChartPoint = {
  timestamp: number;
} & Record<HistoryField, number | null>;

export type ListingFinancialHistory = {
  events: HistoryEvent[];
  chart: {
    fields: HistoryField[];
    points: HistoryChartPoint[];
    spanMs: number;
  } | null;
};

const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

type FieldState = { value: number | null; established: boolean };

function amountOf(value: HistoryAmount): number | null {
  if (value == null || value === "") return null;
  const amount = typeof value === "bigint" ? Number(value) : Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function timeOf(value: Date | string): number {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function calendarDay(time: number): string {
  const date = new Date(time);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function emptyPoint(timestamp: number): HistoryChartPoint {
  return {
    timestamp,
    askingPrice: null,
    revenue: null,
    profit: null,
    sde: null,
    ebitda: null,
  };
}

/**
 * A line chart is only worth showing when a number moved and the snapshots
 * span more than one calendar day. Same-day edits stay in the change list.
 */
export function buildListingFinancialHistory(
  snapshots: HistorySnapshot[],
): ListingFinancialHistory | null {
  if (snapshots.length < 2) return null;

  const chronological = [...snapshots].sort(
    (a, b) => timeOf(a.createdAt) - timeOf(b.createdAt) || a.id - b.id,
  );

  const state = new Map<HistoryField, FieldState>(
    HISTORY_FIELDS.map((field) => [field, { value: null, established: false }]),
  );
  const events: HistoryEvent[] = [];

  for (const snapshot of chronological) {
    const changes: HistoryChange[] = [];

    for (const field of HISTORY_FIELDS) {
      const next = amountOf(snapshot[field]);
      const current = state.get(field) ?? { value: null, established: false };

      if (!current.established && next != null) {
        state.set(field, { value: next, established: true });
        continue;
      }

      if (current.established && current.value != null && next == null) {
        changes.push({ field, kind: "removed" });
        state.set(field, { value: null, established: true });
        continue;
      }

      if (current.established && current.value == null && next != null) {
        changes.push({ field, kind: "set", to: next });
        state.set(field, { value: next, established: true });
        continue;
      }

      if (
        current.established &&
        current.value != null &&
        next != null &&
        current.value !== next
      ) {
        changes.push({
          field,
          kind: "change",
          from: current.value,
          to: next,
          increased: next > current.value,
        });
        state.set(field, { value: next, established: true });
      }
    }

    if (changes.length > 0) {
      events.push({
        id: snapshot.id,
        createdAt: new Date(timeOf(snapshot.createdAt)),
        source: snapshot.source,
        changes,
      });
    }
  }

  if (events.length === 0) return null;

  const fields = HISTORY_FIELDS.filter((field) => {
    const values = new Set<number>();
    for (const snapshot of chronological) {
      const amount = amountOf(snapshot[field]);
      if (amount != null) values.add(amount);
    }
    return values.size >= 2;
  });

  let chart: ListingFinancialHistory["chart"] = null;
  if (fields.length > 0) {
    const points: HistoryChartPoint[] = [];
    let started = false;

    for (const snapshot of chronological) {
      const point = emptyPoint(timeOf(snapshot.createdAt));
      let hasValue = false;
      for (const field of fields) {
        const amount = amountOf(snapshot[field]);
        point[field] = amount;
        if (amount != null) hasValue = true;
      }
      if (!started && !hasValue) continue;
      if (hasValue) started = true;
      points.push(point);
    }

    const first = points[0];
    const last = points[points.length - 1];
    if (
      first &&
      last &&
      points.length >= 2 &&
      calendarDay(first.timestamp) !== calendarDay(last.timestamp)
    ) {
      chart = {
        fields,
        points,
        spanMs: last.timestamp - first.timestamp,
      };
    }
  }

  return { events: events.reverse().slice(0, 20), chart };
}

export function formatHistoryAxisTick(
  timestamp: number,
  spanMs: number,
): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(spanMs >= YEAR_MS ? { year: "numeric" } : {}),
  });
}

/** One tick per calendar day in the series, capped so labels do not repeat. */
export function historyAxisTicks(
  timestamps: number[],
  maxTicks = 5,
): number[] {
  const days: number[] = [];
  const seen = new Set<string>();
  for (const timestamp of timestamps) {
    const key = calendarDay(timestamp);
    if (seen.has(key)) continue;
    seen.add(key);
    days.push(timestamp);
  }

  if (days.length <= maxTicks) return days;

  const lastIndex = days.length - 1;
  const ticks = [days[0]!];
  const inner = maxTicks - 2;
  for (let step = 1; step <= inner; step++) {
    const index = Math.round((lastIndex * step) / (inner + 1));
    const stamp = days[index];
    if (stamp != null && stamp !== ticks[ticks.length - 1]) ticks.push(stamp);
  }
  const last = days[lastIndex];
  if (last != null && last !== ticks[ticks.length - 1]) ticks.push(last);
  return ticks;
}
